import { Box, Card, CardContent, CardHeader, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/styles';
import moment from 'moment';
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Countdown from 'react-countdown';
import { useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import TestOMRSheet from '../../../../components/common/OnlineTest/TestOMRSheet';
import { ConvertToBase64 } from '../../../../helper/commonHelper';
import { uploadFile } from '../../../../service/upload.file.service';
import { getTestById } from '../../../../service/test.service';
import InAppTest from './Student.InAppTest';
import { testSubmission } from '../../../../service/test.submission.service';
import TestUploadContainer from '../../../../components/common/OnlineTest/TestUploadContainer';
import DownloadFileContainer from '../../../../components/DownloadFileContainer';

const RootStyle = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden'
});

export default function OnlineTest() {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.currentUser);
  const params = useParams();
  // Loading state
  const [loading, setLoading] = useState(true);
  // Test details
  const [testDetails, setTestDetails] = useState(null);
  // Question details
  const [questions, setQuestions] = useState([]);
  // Test submitting
  const [testSubmitting, setSubmitting] = useState(false);
  // Is already submited
  const [isAlreadySubmited, setAlreadySubmited] = useState(false);
  // File state
  const [selectedFile, setSelectedFile] = useState(null);
  // File submission btn
  const [isFileBtnLoading, setFileBtnLoading] = useState(false);
  // Notification
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchTestDetail = async () => {
      const res = await getTestById(params.id);

      if (res) {
        if (res.type === 'type') {
          const details = res.questions.map((ques) => {
            const newOptions = ques.options.map((opt) => ({ ...opt, isSelected: false }));
            return { ...ques, options: newOptions };
          });
          setQuestions(details);
        }

        if (res.type === 'upload' && res.answerSheet.length > 0) {
          const details = res.answerSheet.map((ques) => {
            const newOptions = ques.options.map((opt) => ({ ...opt, isSelected: false }));
            return { ...ques, options: newOptions };
          });
          setQuestions(details);
        }
        setTestDetails(res);
        setAlreadySubmited(!!res?.submissionId);
        setLoading(false);
      }
    };

    if (testDetails === null) fetchTestDetail();
  }, []);

  const testEndTiming = useMemo(() => {
    if (testDetails) {
      return moment(testDetails.startTime).add(testDetails.duration, 'minute');
    }
    return null;
  }, [testDetails]);

  useEffect(() => {
    if (moment().isAfter(testEndTiming)) {
      setTimeout(() => {
        navigate(`/dashboard/classes/${testDetails.classId}`, {
          replace: true,
          state: null
        });
      }, 2000);
    }
  }, [testEndTiming]);

  useEffect(() => {
    if (isAlreadySubmited) {
      setTimeout(() => {
        navigate(`/dashboard/classes/${testDetails.classId}`, {
          replace: true,
          state: null
        });
      }, 2000);
    }
  }, [isAlreadySubmited]);

  const handleSubmit = async () => {
    setSubmitting(true);
    const newAnswerArray = questions.map((qu) => ({
      name: qu.name,
      options: qu.options.map((op) => ({
        value: op.value,
        isAnswer: op.isAnswer,
        isSelected: op.isSelected
      }))
    }));
    const submissionObj = {
      orgId: testDetails.orgId,
      testId: testDetails.id,
      studentId: currentUser.id,
      questionsAndAnswers: newAnswerArray
    };

    const res = await testSubmission(testDetails.classId, submissionObj);
    if (res) {
      setSubmitting(false);
      enqueueSnackbar('Test is submitted', {
        variant: 'success'
      });
      navigate(`/dashboard/classes/${testDetails.classId}`, {
        replace: true,
        state: null
      });
    } else {
      enqueueSnackbar('Something went wrong please try again', {
        variant: 'error'
      });
      setSubmitting(false);
    }
  };

  const onFileSubmit = async () => {
    setFileBtnLoading(true);
    const base64 = await ConvertToBase64(selectedFile);
    const fileObj = {
      base64,
      fileType: selectedFile.type,
      fileName: selectedFile.name,
      uploadFor: 'testSubmission',
      userId: currentUser.id,
      orgId: testDetails.orgId
    };
    const fileRes = await uploadFile(testDetails.classId, fileObj);
    if (fileRes) {
      const submissionObj = {
        orgId: testDetails.orgId,
        testId: testDetails.id,
        studentId: currentUser.id,
        submittedURL: fileRes.fileURL
        // duration: moment(new Date()).diff(moment(startDateTime), 'minutes')
      };
      const res = await testSubmission(testDetails.classId, submissionObj);
      if (res) {
        setFileBtnLoading(false);
        enqueueSnackbar('Test is submitted', {
          variant: 'success'
        });
        navigate(`/dashboard/classes/${testDetails.classId}`, {
          replace: true,
          state: null
        });
      } else {
        setFileBtnLoading(false);
        enqueueSnackbar('Something went wrong please try again', {
          variant: 'error'
        });
      }
    } else {
      enqueueSnackbar('Something went wrong please try again', {
        variant: 'error'
      });
    }
  };

  return (
    <RootStyle
      title="Fainlearn | Online Test"
      isDivRender={false}
      style={{
        display: `${loading ? 'flex' : ''}`,
        width: `${loading ? '100%' : ''}`,
        height: `${loading ? '100%' : ''}`,
        justifyContent: `${loading ? 'center' : ''}`,
        alignItems: `${loading ? 'center' : ''}`
      }}
    >
      {/* If loading */}
      {loading && <CircularProgress />}
      {/* If test is ended */}
      {!loading && moment().isAfter(testEndTiming) && (
        <Box width="100%" maxWidth="700px" margin="0 auto">
          <Typography fontWeight={700} my={5} fontSize={30} textAlign="center">
            Test is already ended
          </Typography>
          <Typography textAlign="center">You will be redirect to class</Typography>
        </Box>
      )}
      {/* If test is already submitted */}
      {!loading && isAlreadySubmited && (
        <Box width="100%" maxWidth="700px" margin="0 auto">
          <Typography fontWeight={700} my={5} fontSize={30} textAlign="center">
            You already given this test.
          </Typography>
          <Typography textAlign="center">You will be redirect to class</Typography>
        </Box>
      )}
      {/* When test is live and not submitted */}
      {!loading && !isAlreadySubmited && !moment().isAfter(testEndTiming) && (
        <Box width="100%" maxWidth="700px" margin="0 auto">
          <Typography fontWeight={700} my={5} fontSize={30}>
            {testDetails.name}
          </Typography>
          <Typography mb={2}>Total Mark: {testDetails.totalMark}</Typography>
          <Countdown
            date={moment(testEndTiming).format()}
            renderer={(props) => (
              <Typography
                textAlign="end"
                mb={3}
                // eslint-disable-next-line react/prop-types
              >{`Remaining time ${props?.hours}:${props?.minutes}:${props?.seconds}`}</Typography>
            )}
            onComplete={() => handleSubmit()}
          />
          {/* Is test type is upload */}
          {testDetails.type === 'upload' && (
            <>
              <Card sx={{ marginTop: 2 }}>
                <CardHeader title="Download the test paper" />
                <CardContent>
                  <DownloadFileContainer
                    title={testDetails.typeURL}
                    fileLink={testDetails.typeURL}
                    isDownload
                  />
                </CardContent>
              </Card>

              {/* If upload test is not have omr sheet */}
              {testDetails.answerSheet.length === 0 && (
                <TestUploadContainer
                  testDetails={testDetails}
                  selectedFile={selectedFile}
                  setSelectedFile={setSelectedFile}
                />
              )}
              {/* If upload test is have omr sheet */}
              {testDetails.answerSheet.length > 0 && (
                <TestOMRSheet
                  numOfAns={testDetails.optionsPerQuestion}
                  questionsArray={questions}
                  updateQuestions={setQuestions}
                />
              )}
              <Box mt={3} textAlign="center">
                <LoadingButton
                  loading={isFileBtnLoading}
                  onClick={
                    testDetails.answerSheet.length > 0 ? () => handleSubmit() : () => onFileSubmit()
                  }
                  variant="contained"
                  style={{ borderRadius: '20px', width: '45%' }}
                >
                  Submit
                </LoadingButton>
              </Box>
            </>
          )}
          {testDetails.type === 'type' && (
            <InAppTest
              testQuestion={questions}
              updateQuestions={setQuestions}
              submitTest={() => handleSubmit()}
              testSubmitting={testSubmitting}
            />
          )}
        </Box>
      )}
    </RootStyle>
  );
}
